import { useEffect, useState, useCallback, useRef } from "react";
import AdminLayout from "../layout/AdminLayout";
import { API_BASE_URL } from "../../config/api";
import { adminAuthHeader } from "../../utils/authHeader";

const LEVEL_CONFIG = {
  red:    { label: "🔴 Red Alert — Critical",  bg: "#fee2e2", border: "#ef4444", color: "#991b1b", badge: "#ef4444" },
  orange: { label: "🟠 Orange Alert — Warning", bg: "#fff7ed", border: "#f97316", color: "#9a3412", badge: "#f97316" },
  green:  { label: "🟢 Green — General Notice", bg: "#f0fdf4", border: "#22c55e", color: "#166534", badge: "#22c55e" },
};

// ── Fix URL — make sure it has https:// ──────────
const safeUrl = (url) => {
  if (!url) return "#";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return "https://" + url;
};

// ── Searchable dropdown ───────────────────────────
const SearchDropdown = ({ options, value, onChange, placeholder }) => {
  const [search, setSearch] = useState("");
  const [open,   setOpen]   = useState(false);
  const ref = useRef(null);
  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div onClick={() => setOpen(!open)} style={{ ...iStyle, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: value ? "#1b2559" : "#9ca3af" }}>{value || placeholder}</span>
        <span style={{ color: "#a3aed0" }}>▾</span>
      </div>
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 999, background: "#fff", border: "1.5px solid #e0e5f2", borderRadius: "10px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", marginTop: "4px", overflow: "hidden" }}>
          <div style={{ padding: "8px" }}><input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." style={{ ...iStyle, margin: 0 }} /></div>
          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            {filtered.length === 0
              ? <div style={{ padding: "12px 16px", color: "#a3aed0", fontSize: "13px" }}>No results</div>
              : filtered.map((o, i) => (
                <div key={i} onClick={() => { onChange(o); setOpen(false); setSearch(""); }}
                  style={{ padding: "10px 16px", cursor: "pointer", fontSize: "14px", background: o === value ? "#f0f4ff" : "transparent", color: o === value ? "#4318ff" : "#1b2559", fontWeight: o === value ? "600" : "400" }}>
                  {o}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Multi-owner selector ──────────────────────────
const MultiOwnerSelector = ({ owners, selected, onChange }) => {
  const [search, setSearch] = useState("");
  const filtered = owners.filter(o =>
    o.name.toLowerCase().includes(search.toLowerCase()) ||
    o.email.toLowerCase().includes(search.toLowerCase()) ||
    (o.city || "").toLowerCase().includes(search.toLowerCase())
  );
  const toggle    = (id) => onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);
  const selectAll = () => onChange(filtered.map(o => o._id));
  const clearAll  = () => onChange([]);
  return (
    <div style={{ border: "1.5px solid #e0e5f2", borderRadius: "12px", overflow: "hidden" }}>
      <div style={{ padding: "10px", borderBottom: "1px solid #f0f0f0", background: "#f9faff" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search by name, email or city..." style={{ ...iStyle, margin: 0, background: "#fff" }} />
      </div>
      <div style={{ padding: "8px 12px", borderBottom: "1px solid #f0f0f0", display: "flex", gap: "10px", alignItems: "center" }}>
        <span style={{ fontSize: "12px", color: "#6b7280" }}>{selected.length} selected</span>
        <button type="button" onClick={selectAll} style={{ fontSize: "12px", color: "#4318ff", background: "none", border: "none", cursor: "pointer", fontWeight: "600" }}>Select All ({filtered.length})</button>
        {selected.length > 0 && <button type="button" onClick={clearAll} style={{ fontSize: "12px", color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontWeight: "600" }}>Clear All</button>}
      </div>
      <div style={{ maxHeight: "220px", overflowY: "auto" }}>
        {filtered.map(o => (
          <div key={o._id} onClick={() => toggle(o._id)}
            style={{ padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: "12px", background: selected.includes(o._id) ? "#f0f4ff" : "transparent", borderBottom: "1px solid #f9f9f9" }}>
            <div style={{ width: "18px", height: "18px", borderRadius: "5px", flexShrink: 0, border: selected.includes(o._id) ? "2px solid #4318ff" : "2px solid #d1d5db", background: selected.includes(o._id) ? "#4318ff" : "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {selected.includes(o._id) && <span style={{ color: "#fff", fontSize: "11px" }}>✓</span>}
            </div>
            <div>
              <div style={{ fontWeight: "600", fontSize: "13px", color: "#1b2559" }}>{o.name}</div>
              <div style={{ fontSize: "11px", color: "#9ca3af" }}>{o.email} {o.city ? `· ${o.city}` : ""}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Edit Alert Modal ──────────────────────────────
const EditModal = ({ alert, onClose, onSaved }) => {
  const [title,        setTitle]        = useState(alert.title);
  const [message,      setMessage]      = useState(alert.message);
  const [level,        setLevel]        = useState(alert.level);
  const [videoUrl,     setVideoUrl]     = useState(alert.videoUrl || "");
  const [allowReply,   setAllowReply]   = useState(alert.allowReply || false);
  const [imageFile,    setImageFile]    = useState(null);
  const [imagePreview, setImagePreview] = useState(alert.imageUrl || "");
  const [saving,       setSaving]       = useState(false);
  const [msg,          setMsg]          = useState("");

  const handleSave = async () => {
    if (!title.trim() || !message.trim()) return setMsg("⚠️ Title and message required.");
    setSaving(true); setMsg("");
    const formData = new FormData();
    formData.append("title",      title);
    formData.append("message",    message);
    formData.append("level",      level);
    formData.append("videoUrl",   videoUrl);
    formData.append("allowReply", allowReply);
    if (imageFile) formData.append("image", imageFile);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/alerts/${alert._id}/edit`, {
        method: "PUT", headers: adminAuthHeader(), body: formData,
      });
      const data = await res.json();
      if (res.ok) { setMsg("✅ Saved!"); setTimeout(() => { onSaved(); onClose(); }, 1000); }
      else setMsg(`⚠️ ${data.message}`);
    } catch { setMsg("⚠️ Server error"); }
    setSaving(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "18px", width: "100%", maxWidth: "560px", maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: "18px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, color: "#1b2559", fontSize: "16px" }}>✏️ Edit Alert</h3>
          <button onClick={onClose} style={{ background: "#f4f7fe", border: "none", borderRadius: "8px", padding: "6px 12px", cursor: "pointer", fontSize: "16px" }}>✕</button>
        </div>
        {/* Body */}
        <div style={{ overflowY: "auto", flex: 1, padding: "20px" }}>
          {msg && <div style={{ padding: "10px 14px", borderRadius: "8px", marginBottom: "14px", fontSize: "14px", background: msg.startsWith("✅") ? "#f0fdf4" : "#fee2e2", color: msg.startsWith("✅") ? "#166534" : "#991b1b" }}>{msg}</div>}

          {/* Level */}
          <div style={{ marginBottom: "16px" }}>
            <label style={lStyle}>Alert Level</label>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {Object.entries(LEVEL_CONFIG).map(([key, cfg]) => (
                <button key={key} type="button" onClick={() => setLevel(key)}
                  style={{ padding: "7px 14px", borderRadius: "20px", border: `2px solid ${cfg.border}`, background: level === key ? cfg.border : cfg.bg, color: level === key ? "#fff" : cfg.color, fontWeight: "600", cursor: "pointer", fontSize: "12px" }}>
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <label style={lStyle}>Title *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} style={iStyle} />
          </div>
          <div style={{ marginBottom: "12px" }}>
            <label style={lStyle}>Message *</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} style={{ ...iStyle, height: "auto", resize: "vertical" }} />
          </div>

          {/* Image */}
          <div style={{ marginBottom: "12px" }}>
            <label style={lStyle}>📷 Proof Image</label>
            {imagePreview && (
              <div style={{ marginBottom: "8px", position: "relative", display: "inline-block" }}>
                <img src={imagePreview} alt="current" style={{ maxWidth: "160px", borderRadius: "8px", border: "1px solid #e0e5f2" }} />
                <button type="button" onClick={() => { setImageFile(null); setImagePreview(""); }}
                  style={{ position: "absolute", top: "-6px", right: "-6px", background: "#ef4444", color: "#fff", border: "none", borderRadius: "50%", width: "18px", height: "18px", cursor: "pointer", fontSize: "10px" }}>✕</button>
              </div>
            )}
            <input type="file" accept="image/*" onChange={e => {
              const f = e.target.files[0];
              if (!f) return;
              setImageFile(f);
              setImagePreview(URL.createObjectURL(f));
            }} style={{ fontSize: "13px", display: "block" }} />
          </div>

          {/* Video URL */}
          <div style={{ marginBottom: "12px" }}>
            <label style={lStyle}>🔗 Video / Link</label>
            <input value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="https://youtube.com/..." style={iStyle} />
          </div>

          {/* Allow Reply */}
          <div style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px" }}>
            <div onClick={() => setAllowReply(!allowReply)}
              style={{ width: "44px", height: "24px", borderRadius: "12px", background: allowReply ? "#4318ff" : "#d1d5db", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
              <div style={{ position: "absolute", top: "3px", left: allowReply ? "23px" : "3px", width: "18px", height: "18px", borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
            </div>
            <span style={{ fontSize: "14px", fontWeight: "600", color: "#1b2559" }}>Allow Owners to Reply</span>
          </div>

          <button onClick={handleSave} disabled={saving}
            style={{ ...btnStyle, width: "100%" }}>
            {saving ? "Saving..." : "💾 Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Replies Modal (with admin re-reply) ───────────
const RepliesModal = ({ alert, onClose, onUpdated }) => {
  const [replyingTo,  setReplyingTo]  = useState(null); // replyId
  const [adminMsg,    setAdminMsg]    = useState("");
  const [sending,     setSending]     = useState(false);
  const [statusMsg,   setStatusMsg]   = useState("");

  if (!alert) return null;
  const cfg = LEVEL_CONFIG[alert.level] || LEVEL_CONFIG.green;

  const sendAdminReply = async (replyId) => {
    if (!adminMsg.trim()) return setStatusMsg("⚠️ Type a message first.");
    setSending(true); setStatusMsg("");
    try {
      const res  = await fetch(`${API_BASE_URL}/api/alerts/${alert._id}/replies/${replyId}/admin-reply`, {
        method:  "POST",
        headers: { ...adminAuthHeader(), "Content-Type": "application/json" },
        body:    JSON.stringify({ message: adminMsg }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatusMsg("✅ Reply sent!");
        setAdminMsg(""); setReplyingTo(null);
        if (onUpdated) onUpdated();
      } else setStatusMsg(`⚠️ ${data.message}`);
    } catch { setStatusMsg("⚠️ Server error"); }
    setSending(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "18px", width: "100%", maxWidth: "600px", maxHeight: "88vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "18px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <span style={{ background: cfg.badge, color: "#fff", padding: "2px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "700" }}>{cfg.label}</span>
            <h3 style={{ margin: "6px 0 2px", color: "#1b2559", fontSize: "15px" }}>{alert.title}</h3>
            <p style={{ margin: 0, fontSize: "12px", color: "#a3aed0" }}>{alert.replies?.length || 0} owner repl{alert.replies?.length === 1 ? "y" : "ies"}</p>
          </div>
          <button onClick={onClose} style={{ background: "#f4f7fe", border: "none", borderRadius: "8px", padding: "6px 12px", cursor: "pointer", fontSize: "16px" }}>✕</button>
        </div>

        {/* Status msg */}
        {statusMsg && (
          <div style={{ padding: "10px 20px", background: statusMsg.startsWith("✅") ? "#f0fdf4" : "#fee2e2", color: statusMsg.startsWith("✅") ? "#166534" : "#991b1b", fontSize: "13px" }}>{statusMsg}</div>
        )}

        {/* Replies list */}
        <div style={{ overflowY: "auto", flex: 1, padding: "16px 20px" }}>
          {!alert.replies?.length ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#a3aed0" }}>
              <div style={{ fontSize: "36px", marginBottom: "8px" }}>💬</div>
              <p>No owner replies yet</p>
            </div>
          ) : alert.replies.map((r) => (
            <div key={r._id} style={{ marginBottom: "16px" }}>

              {/* Owner reply bubble */}
              <div style={{ background: "#f9faff", borderRadius: "12px", padding: "14px", border: "1px solid #e0e5f2" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "#4318ff", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "700", fontSize: "13px" }}>
                      {(r.ownerName || "O").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p style={{ margin: 0, fontWeight: "700", fontSize: "14px", color: "#1b2559" }}>{r.ownerName || "Owner"}</p>
                      <p style={{ margin: 0, fontSize: "11px", color: "#a3aed0" }}>{new Date(r.createdAt).toLocaleString("en-IN")}</p>
                    </div>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: "14px", color: "#374151", lineHeight: "1.6" }}>{r.message}</p>
                {r.imageUrl && <img src={r.imageUrl} alt="owner attachment" style={{ marginTop: "10px", maxWidth: "100%", borderRadius: "8px" }} />}
              </div>

              {/* Admin's reply to this owner */}
              {r.adminReply?.message ? (
                <div style={{ marginTop: "8px", marginLeft: "24px", background: "#f0f4ff", borderRadius: "10px", padding: "12px 14px", border: "1px solid #c7d2fe" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#4318ff" }}>👑 Admin replied</span>
                    <span style={{ fontSize: "11px", color: "#a3aed0" }}>{new Date(r.adminReply.repliedAt).toLocaleString("en-IN")}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: "14px", color: "#374151" }}>{r.adminReply.message}</p>
                  {/* Edit admin reply — re-open input */}
                  <button onClick={() => { setReplyingTo(r._id); setAdminMsg(r.adminReply.message); }}
                    style={{ marginTop: "8px", fontSize: "12px", color: "#4318ff", background: "none", border: "none", cursor: "pointer", fontWeight: "600" }}>
                    ✏️ Edit Reply
                  </button>
                </div>
              ) : (
                !replyingTo && (
                  <button onClick={() => { setReplyingTo(r._id); setAdminMsg(""); setStatusMsg(""); }}
                    style={{ marginTop: "8px", marginLeft: "24px", fontSize: "13px", color: "#4318ff", background: "#f0f4ff", border: "1px solid #c7d2fe", borderRadius: "8px", padding: "5px 14px", cursor: "pointer", fontWeight: "600" }}>
                    💬 Reply to {r.ownerName}
                  </button>
                )
              )}

              {/* Admin reply input */}
              {replyingTo === r._id && (
                <div style={{ marginTop: "10px", marginLeft: "24px", background: "#fff", border: "1.5px solid #4318ff", borderRadius: "10px", padding: "12px" }}>
                  <p style={{ margin: "0 0 8px", fontSize: "12px", fontWeight: "700", color: "#4318ff" }}>
                    👑 Replying to {r.ownerName}
                  </p>
                  <textarea value={adminMsg} onChange={e => setAdminMsg(e.target.value)}
                    placeholder="Type your reply to this owner..."
                    rows={3}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1.5px solid #e0e5f2", fontSize: "14px", outline: "none", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }} />
                  <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                    <button onClick={() => sendAdminReply(r._id)} disabled={sending}
                      style={{ ...btnStyle, padding: "8px 18px", fontSize: "13px" }}>
                      {sending ? "Sending..." : "📤 Send"}
                    </button>
                    <button onClick={() => { setReplyingTo(null); setAdminMsg(""); setStatusMsg(""); }}
                      style={{ ...cancelStyle, padding: "8px 14px", fontSize: "13px" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────
const AdminAlerts = () => {
  const [alerts,      setAlerts]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [msg,         setMsg]         = useState("");
  const [owners,      setOwners]      = useState([]);
  const [cities,      setCities]      = useState([]);
  const [editAlert,   setEditAlert]   = useState(null);
  const [viewReplies, setViewReplies] = useState(null);

  const [title,        setTitle]        = useState("");
  const [message,      setMessage]      = useState("");
  const [level,        setLevel]        = useState("green");
  const [targetType,   setTargetType]   = useState("all");
  const [targetCity,   setTargetCity]   = useState("");
  const [targetState,  setTargetState]  = useState("");
  const [targetOwners, setTargetOwners] = useState([]);
  const [imageFile,    setImageFile]    = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [videoUrl,     setVideoUrl]     = useState("");
  const [allowReply,   setAllowReply]   = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/alerts/admin`, { headers: adminAuthHeader() });
      const data = await res.json();
      if (res.ok) setAlerts(data.alerts || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAlerts();
    fetch(`${API_BASE_URL}/api/alerts/owners-list`,    { headers: adminAuthHeader() }).then(r => r.json()).then(d => setOwners(d.owners || [])).catch(() => {});
    fetch(`${API_BASE_URL}/api/alerts/locations-list`, { headers: adminAuthHeader() }).then(r => r.json()).then(d => setCities(d.cities || [])).catch(() => {});
  }, [fetchAlerts]);

  const resetForm = () => {
    setTitle(""); setMessage(""); setLevel("green"); setTargetType("all");
    setTargetCity(""); setTargetState(""); setTargetOwners([]);
    setImageFile(null); setImagePreview(""); setVideoUrl(""); setAllowReply(false); setMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) return setMsg("⚠️ Title and message are required.");
    if (targetType === "city"   && !targetCity)               return setMsg("⚠️ Please select a city.");
    if (targetType === "state"  && !targetState.trim())       return setMsg("⚠️ Please enter a state.");
    if (targetType === "owners" && targetOwners.length === 0) return setMsg("⚠️ Please select at least one owner.");

    setSubmitting(true); setMsg("");
    const fd = new FormData();
    fd.append("title", title); fd.append("message", message); fd.append("level", level);
    fd.append("targetType", targetType); fd.append("targetCity", targetCity);
    fd.append("targetState", targetState); fd.append("videoUrl", videoUrl);
    fd.append("allowReply", allowReply);
    targetOwners.forEach(id => fd.append("targetOwners", id));
    if (imageFile) fd.append("image", imageFile);

    try {
      const res  = await fetch(`${API_BASE_URL}/api/alerts`, { method: "POST", headers: adminAuthHeader(), body: fd });
      const data = await res.json();
      if (res.ok) { setMsg(`✅ ${data.message}`); resetForm(); fetchAlerts(); setTimeout(() => { setShowForm(false); setMsg(""); }, 2500); }
      else setMsg(`⚠️ ${data.message}`);
    } catch { setMsg("⚠️ Cannot connect to server."); }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this alert?")) return;
    try {
      await fetch(`${API_BASE_URL}/api/alerts/${id}`, { method: "DELETE", headers: adminAuthHeader() });
      setAlerts(prev => prev.filter(a => a._id !== id));
    } catch {}
  };

  return (
    <AdminLayout>
      <div className="admin-page-wrapper">

        <div className="admin-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <p className="admin-welcome">Alerts</p>
            <h2 className="admin-title">Manage Alerts</h2>
          </div>
          <button onClick={() => { setShowForm(!showForm); resetForm(); }} style={btnStyle}>
            {showForm ? "✕ Cancel" : "+ Create Alert"}
          </button>
        </div>

        {/* CREATE FORM */}
        {showForm && (
          <div style={{ background: "#fff", borderRadius: "16px", padding: "24px", marginBottom: "24px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", border: "1.5px solid #e0e5f2" }}>
            <h3 style={{ marginTop: 0, color: "#1b2559" }}>Create New Alert</h3>
            {msg && <div style={{ padding: "10px 14px", borderRadius: "8px", marginBottom: "16px", fontSize: "14px", background: msg.startsWith("✅") ? "#f0fdf4" : "#fee2e2", color: msg.startsWith("✅") ? "#166534" : "#991b1b" }}>{msg}</div>}
            <form onSubmit={handleSubmit}>

              <div style={{ marginBottom: "18px" }}>
                <label style={lStyle}>Alert Level</label>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  {Object.entries(LEVEL_CONFIG).map(([key, cfg]) => (
                    <button key={key} type="button" onClick={() => setLevel(key)}
                      style={{ padding: "8px 18px", borderRadius: "20px", border: `2px solid ${cfg.border}`, background: level === key ? cfg.border : cfg.bg, color: level === key ? "#fff" : cfg.color, fontWeight: "600", cursor: "pointer", fontSize: "13px" }}>
                      {cfg.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: "14px" }}>
                <label style={lStyle}>Alert Title *</label>
                <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Fraudster Active in Chennai — Be Alert" style={iStyle} required />
              </div>
              <div style={{ marginBottom: "14px" }}>
                <label style={lStyle}>Message *</label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} style={{ ...iStyle, height: "auto", resize: "vertical" }} required />
              </div>
              <div style={{ marginBottom: "14px" }}>
                <label style={lStyle}>📷 Proof Image (optional)</label>
                <input type="file" accept="image/*" onChange={e => { const f = e.target.files[0]; if (!f) return; setImageFile(f); setImagePreview(URL.createObjectURL(f)); }} style={{ fontSize: "13px" }} />
                {imagePreview && (
                  <div style={{ marginTop: "8px", position: "relative", display: "inline-block" }}>
                    <img src={imagePreview} alt="preview" style={{ maxWidth: "180px", borderRadius: "8px", border: "1px solid #e0e5f2" }} />
                    <button type="button" onClick={() => { setImageFile(null); setImagePreview(""); }}
                      style={{ position: "absolute", top: "-6px", right: "-6px", background: "#ef4444", color: "#fff", border: "none", borderRadius: "50%", width: "18px", height: "18px", cursor: "pointer", fontSize: "10px" }}>✕</button>
                  </div>
                )}
              </div>
              <div style={{ marginBottom: "14px" }}>
                <label style={lStyle}>🔗 Video / Link (optional)</label>
                <input value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="https://youtube.com/... or any link" style={iStyle} />
                <p style={{ fontSize: "11px", color: "#9ca3af", marginTop: "4px" }}>Tip: Make sure URL starts with https://</p>
              </div>
              <div style={{ marginBottom: "18px", display: "flex", alignItems: "center", gap: "12px" }}>
                <div onClick={() => setAllowReply(!allowReply)}
                  style={{ width: "44px", height: "24px", borderRadius: "12px", background: allowReply ? "#4318ff" : "#d1d5db", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                  <div style={{ position: "absolute", top: "3px", left: allowReply ? "23px" : "3px", width: "18px", height: "18px", borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                </div>
                <div>
                  <span style={{ fontWeight: "600", fontSize: "14px", color: "#1b2559" }}>Allow Owners to Reply</span>
                  <span style={{ fontSize: "12px", color: "#9ca3af", marginLeft: "8px" }}>{allowReply ? "Owners can respond" : "No replies"}</span>
                </div>
              </div>

              <div style={{ marginBottom: "14px" }}>
                <label style={lStyle}>Send To</label>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  {[{ val: "all", lbl: "📢 All Owners" }, { val: "city", lbl: "🏙️ Specific City" }, { val: "state", lbl: "🗺️ Specific State" }, { val: "owners", lbl: "👥 Select Owners" }].map(({ val, lbl }) => (
                    <button key={val} type="button" onClick={() => { setTargetType(val); setTargetOwners([]); }}
                      style={{ padding: "7px 16px", borderRadius: "20px", border: "1.5px solid", borderColor: targetType === val ? "#4318ff" : "#e0e5f2", background: targetType === val ? "#4318ff" : "#f4f7fe", color: targetType === val ? "#fff" : "#1b2559", fontWeight: "600", cursor: "pointer", fontSize: "13px" }}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              {targetType === "city"   && <div style={{ marginBottom: "14px" }}><label style={lStyle}>Select City</label><SearchDropdown options={cities} value={targetCity} onChange={setTargetCity} placeholder="Search and select city..." /></div>}
              {targetType === "state"  && <div style={{ marginBottom: "14px" }}><label style={lStyle}>State Name</label><input value={targetState} onChange={e => setTargetState(e.target.value)} placeholder="e.g. Tamil Nadu" style={iStyle} /></div>}
              {targetType === "owners" && (
                <div style={{ marginBottom: "14px" }}>
                  <label style={lStyle}>Select Owners {targetOwners.length > 0 && <span style={{ marginLeft: "8px", background: "#4318ff", color: "#fff", borderRadius: "10px", padding: "1px 8px", fontSize: "11px" }}>{targetOwners.length} selected</span>}</label>
                  <MultiOwnerSelector owners={owners} selected={targetOwners} onChange={setTargetOwners} />
                  {targetOwners.length > 0 && (
                    <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {targetOwners.map(id => { const o = owners.find(x => x._id === id); return o ? <span key={id} style={{ background: "#f0f4ff", color: "#4318ff", border: "1px solid #c7d2fe", borderRadius: "20px", padding: "3px 10px", fontSize: "12px", display: "flex", alignItems: "center", gap: "6px" }}>{o.name}<span onClick={() => setTargetOwners(p => p.filter(x => x !== id))} style={{ cursor: "pointer", fontWeight: "700" }}>×</span></span> : null; })}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
                <button type="submit" style={btnStyle} disabled={submitting}>{submitting ? "Sending..." : "🚀 Send Alert"}</button>
                <button type="button" onClick={() => { setShowForm(false); resetForm(); }} style={cancelStyle}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* ALERTS LIST */}
        {loading ? (
          <p style={{ color: "#a3aed0", textAlign: "center", padding: "40px" }}>Loading…</p>
        ) : alerts.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px", color: "#a3aed0" }}>
            <div style={{ fontSize: "48px", marginBottom: "12px" }}>🔔</div>
            <p style={{ fontWeight: "600", color: "#1b2559" }}>No alerts yet</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {alerts.map(alert => {
              const cfg = LEVEL_CONFIG[alert.level] || LEVEL_CONFIG.green;
              const pendingReplies = (alert.replies || []).filter(r => !r.adminReply?.message).length;
              return (
                <div key={alert._id} style={{ background: "#fff", borderRadius: "14px", padding: "18px 20px", border: `2px solid ${cfg.border}`, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "10px" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px", alignItems: "center" }}>
                        <span style={{ background: cfg.badge, color: "#fff", padding: "2px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "700" }}>{cfg.label}</span>
                        <span style={{ fontSize: "12px", color: "#6b7280", background: "#f3f4f6", padding: "2px 10px", borderRadius: "10px" }}>
                          {alert.targetType === "all" ? "📢 All Owners" : alert.targetType === "city" ? `🏙️ ${alert.targetCity}` : alert.targetType === "state" ? `🗺️ ${alert.targetState}` : `👥 ${alert.targetOwners?.length || 0} Owner(s)`}
                        </span>
                        {alert.allowReply && <span style={{ fontSize: "11px", background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0", padding: "2px 8px", borderRadius: "10px" }}>💬 Replies on</span>}
                        {alert.imageUrl   && <span style={{ fontSize: "11px", background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", padding: "2px 8px", borderRadius: "10px" }}>📷 Image</span>}
                        {alert.videoUrl   && <span style={{ fontSize: "11px", background: "#faf5ff", color: "#7e22ce", border: "1px solid #e9d5ff", padding: "2px 8px", borderRadius: "10px" }}>🔗 Link</span>}
                        <span style={{ fontSize: "12px", color: "#a3aed0" }}>👁️ {alert.readBy?.length || 0} read</span>
                        <span style={{ fontSize: "12px", color: "#a3aed0" }}>{new Date(alert.createdAt).toLocaleString("en-IN")}</span>
                      </div>
                      <h4 style={{ margin: "0 0 6px", color: "#1b2559", fontSize: "15px" }}>{alert.title}</h4>
                      <p style={{ margin: 0, color: "#4b5563", fontSize: "14px", lineHeight: "1.6" }}>{alert.message}</p>
                      {alert.imageUrl && <img src={alert.imageUrl} alt="proof" style={{ marginTop: "10px", maxWidth: "180px", borderRadius: "8px", border: "1px solid #e0e5f2" }} />}
                      {alert.videoUrl && (
                        <a href={safeUrl(alert.videoUrl)} target="_blank" rel="noreferrer"
                          style={{ display: "inline-block", marginTop: "8px", fontSize: "13px", color: "#4318ff", fontWeight: "600" }}>
                          ▶ View Link / Video
                        </a>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-end", flexShrink: 0 }}>
                      <button onClick={() => setEditAlert(alert)}
                        style={{ background: "#f4f7fe", color: "#1b2559", border: "1px solid #e0e5f2", borderRadius: "8px", padding: "6px 14px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>
                        ✏️ Edit
                      </button>
                      {alert.allowReply && (
                        <button onClick={() => setViewReplies(alert)}
                          style={{ background: pendingReplies > 0 ? "#4318ff" : "#f0f4ff", color: pendingReplies > 0 ? "#fff" : "#4318ff", border: "1px solid #c7d2fe", borderRadius: "8px", padding: "6px 14px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>
                          💬 {alert.replies?.length || 0} Repl{alert.replies?.length === 1 ? "y" : "ies"}
                          {pendingReplies > 0 && <span style={{ marginLeft: "6px", background: "#ef4444", color: "#fff", borderRadius: "10px", padding: "1px 6px", fontSize: "10px" }}>{pendingReplies} new</span>}
                        </button>
                      )}
                      <button onClick={() => handleDelete(alert._id)}
                        style={{ background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: "8px", padding: "6px 14px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editAlert   && <EditModal   alert={editAlert}   onClose={() => setEditAlert(null)}   onSaved={fetchAlerts} />}
      {viewReplies && <RepliesModal alert={viewReplies} onClose={() => setViewReplies(null)} onUpdated={fetchAlerts} />}
    </AdminLayout>
  );
};

const iStyle      = { width: "100%", padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #e0e5f2", fontSize: "14px", outline: "none", background: "#f9faff", boxSizing: "border-box" };
const lStyle      = { display: "block", fontSize: "13px", fontWeight: "600", color: "#1b2559", marginBottom: "6px" };
const btnStyle    = { padding: "10px 22px", background: "#4318ff", color: "#fff", border: "none", borderRadius: "10px", fontWeight: "600", fontSize: "14px", cursor: "pointer" };
const cancelStyle = { padding: "10px 22px", background: "#f4f7fe", color: "#1b2559", border: "none", borderRadius: "10px", fontWeight: "600", fontSize: "14px", cursor: "pointer" };

export default AdminAlerts;
